import { Suspense } from "react";
import { Navigate, RouterProvider, createBrowserRouter, useParams } from "react-router-dom";
import ProtectedRoute from "./protected-route";
import OIDCCallback from "@/components/auth/oidc-callback";
import RouteError from "@/components/elements/route-error";
import LoadingFallback from "@/components/elements/loading-fallback";
import { lazyWithRetry } from "@/lib/lazy-retry";
import { ProjectRoleProvider } from "@/context/project-role-context";

function OnboardingRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={`/settings?tab=project&project=${encodeURIComponent(id)}`} replace />;
}

const Login = lazyWithRetry(() => import("@/components/pages/login"));
const AcceptInvitePage = lazyWithRetry(
  () => import("@/components/pages/accept-invite"),
);
const GenerateApiKey = lazyWithRetry(
  () => import("@/components/pages/generate-api-key"),
);
const ProjectList = lazyWithRetry(() => import("@/components/pages/projects"));
import Chat from "@/components/pages/chat";
const Dashboard = lazyWithRetry(() => import("@/components/pages/dashboard"));
const ArtifactsPage = lazyWithRetry(
  () => import("@/components/pages/knowledge-management"),
);
const Agents = lazyWithRetry(() => import("@/components/pages/agents"));
const CodeOntologyList = lazyWithRetry(
  () => import("@/components/pages/code-ontology-list"),
);
const CodeOntologyGraph = lazyWithRetry(
  () => import("@/components/pages/code-ontology-graph"),
);
const CodeOntologyNodes = lazyWithRetry(
  () => import("@/components/pages/code-ontology-nodes"),
);
const OntologyManagerV2 = lazyWithRetry(
  () => import("@/components/pages/ontology-manager-v2"),
);
const ProjectSettings = lazyWithRetry(
  () => import("@/components/pages/project-settings"),
);
const Settings = lazyWithRetry(() => import("@/components/pages/settings"));
const WebhookLogs = lazyWithRetry(
  () => import("@/components/pages/webhook-logs"),
);
const NotFound = lazyWithRetry(() => import("@/components/pages/not-found"));
const FunctionalOntology = lazyWithRetry(() => import("@/components/pages/functional-ontology"));
const KnowledgeGraph = lazyWithRetry(
  () => import("@/components/pages/knowledge-graph"),
);

function Routes() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <ProtectedRoute />,
      errorElement: (
        <Suspense fallback={<LoadingFallback />}>
          <RouteError />
        </Suspense>
      ),
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectList />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "dashboard/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <Dashboard />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "agents/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Agents />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "chat/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Chat />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          // Threaded deep-link for the default CHAT_AGENT. The literal `t`
          // segment keeps this pattern distinguishable from `chat/:agent/:id`,
          // which is why the agent slug can be dropped from the URL entirely.
          path: "chat/:id/t/:threadId",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Chat />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "chat/:agent/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Chat />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          // Backward-compatible URL — new sends navigate to chat/:id/t/:threadId
          // for CHAT_AGENT, but bookmarks with the older slug still resolve here.
          path: "chat/:agent/:id/:threadId",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Chat />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "knowledge/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <ArtifactsPage />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          // The setup wizard was replaced by Settings → Project Settings; old links land there.
          path: "onboarding/:id",
          element: <OnboardingRedirect />,
          errorElement: <RouteError />,
        },
        {
          path: "code-ontology/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <CodeOntologyList />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "code-ontology/:id/graph/:ontologyId",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <CodeOntologyGraph />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "code-ontology/:id/nodes/:ontologyId",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <CodeOntologyNodes />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "knowledge-graph/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <KnowledgeGraph />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "functional-ontology/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <FunctionalOntology />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "ontology/:id/:type",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <OntologyManagerV2 />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          // Distinct from `settings/:id` (project settings) because it has no
          // param — React Router matches the more specific path.
          path: "settings",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Settings />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "settings/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProjectRoleProvider>
                <ProjectSettings />
              </ProjectRoleProvider>
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          // Old direct routes now fold into Settings tabs. Kept as redirects
          // rather than removed so bookmarks and MCP-onboarding printouts
          // still land on the right screen.
          path: "jira-settings",
          element: <Navigate to="/settings?tab=jira" replace />,
          errorElement: <RouteError />,
        },
        {
          path: "user-management",
          element: <Navigate to="/settings?tab=users" replace />,
          errorElement: <RouteError />,
        },
        {
          path: "webhook-logs",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WebhookLogs />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "accept-invite",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AcceptInvitePage />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
        {
          path: "mcp/generate/key",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <GenerateApiKey />
            </Suspense>
          ),
          errorElement: <RouteError />,
        },
      ],
    },
    {
      path: "/login",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <Login />
        </Suspense>
      ),
      errorElement: <RouteError />,
    },
    {
      path: "/callback",
      element: <OIDCCallback />,
      errorElement: <RouteError />,
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <NotFound />
        </Suspense>
      ),
      errorElement: <RouteError />,
    },
  ]);
  return <RouterProvider router={router} />;
}

export default Routes;
